import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Divider, Grid, useMediaQuery, useTheme } from '@mui/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { MultipleTicketsPriceData, MultipleTicketsUIObjectData, TicketData } from '../../api';
import { OrderPageState } from '../orderPageContext/OrderPageState';
import { MULTIPLE_TICKETS_DETAILS_FIELD_NAME, withBookingFee } from '../core/pricing';
import { asOrderPageData } from '../core/types';
import { customerFacingPriceFormatter } from '../utils';
import NumberSelector from './NumberSelector';

type MultipleTicketsUiObjectProps = {
  uiObject: MultipleTicketsUIObjectData;
  orderState: OrderPageState;
  setMultipleTicketsDetails: (values: Record<string, number>) => void;
  isLoadingDates: boolean;
};

export const MultipleTicketsUiObject = ({
  uiObject,
  orderState,
  setMultipleTicketsDetails,
  isLoadingDates,
}: MultipleTicketsUiObjectProps) => {
  const language = useLanguage();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const visibleTickets = useMemo(() => uiObject.tickets.filter((ticket) => !ticket.archived), [uiObject.tickets]);
  const mainTickets = useMemo(
    () => visibleTickets.filter((ticket) => ticket.counts_toward_total_capacity !== false),
    [visibleTickets],
  );
  const extraTickets = useMemo(
    () => visibleTickets.filter((ticket) => ticket.counts_toward_total_capacity === false),
    [visibleTickets],
  );

  const getInitialValues = (tickets: TicketData[]) =>
    tickets.reduce((acc, ticket) => {
      const multipleTickets = orderState.details.custom_details[MULTIPLE_TICKETS_DETAILS_FIELD_NAME] as Record<
        string,
        number
      >;
      if (multipleTickets) {
        acc[ticket.id] = multipleTickets[ticket.id];
      } else if (ticket.default_ticket_group_size) {
        acc[ticket.id] = ticket.default_ticket_group_size;
      } else {
        acc[ticket.id] = 0;
      }

      return acc;
    }, {} as Record<string, number>);

  const debouncedSetMultipleTicketsDetails = useCallback(
    debounce((newValues: Record<string, number>) => {
      setMultipleTicketsDetails(newValues);
    }, 300),
    [setMultipleTicketsDetails],
  );

  const [ticketValues, setTicketValues] = useState(getInitialValues(visibleTickets));
  // retrigger calculation of initial values on change of order page
  useEffect(() => {
    setTicketValues(getInitialValues(visibleTickets));
  }, [orderState.orderPage]);

  // Auto-populate order state with default values only once when component mounts
  useEffect(() => {
    const hasDefaults = visibleTickets.some((ticket) => ticket.default_ticket_group_size !== undefined);
    const currentMultipleTickets = orderState.details.custom_details[MULTIPLE_TICKETS_DETAILS_FIELD_NAME] as Record<
      string,
      number
    >;

    // Only set defaults if they don't already exist in order state
    if (hasDefaults && !currentMultipleTickets) {
      const defaultValues = getInitialValues(visibleTickets);
      debouncedSetMultipleTicketsDetails(defaultValues);
    }
  }, []); // Empty dependency array - runs only once

  const handleValueChange = (id: string, value: number) => {
    const newValues = { ...ticketValues, [id]: value };
    setTicketValues(newValues);
    debouncedSetMultipleTicketsDetails(newValues);
  };
  const prices = asOrderPageData(orderState.orderPage)?.price.find(
    (x): x is MultipleTicketsPriceData => x.type === 'multiple_tickets_price',
  )?.options;
  const totalTickets = Object.values(ticketValues).reduce((sum, val) => sum + val, 0);
  const isAtMaxParticipants = !!uiObject.max_total_participants && totalTickets >= uiObject.max_total_participants;
  const shouldShowMinAlert = totalTickets < uiObject.min_total_participants && uiObject.min_total_alert?.[language];
  const shouldShowMaxAlert = shouldShowMinAlert && uiObject.max_total_alert?.[language];

  const unitPriceFor = (ticketId: string): number =>
    prices?.find((price) => price.ticket_id === ticketId)?.price_per_unit ?? 0;

  const renderTicket = (ticket: TicketData) => (
    <Grid container key={ticket.id} alignItems="center" wrap="nowrap" gap={2}>
      <Grid item xs={9} sx={{ minWidth: 0 }}>
        <OrderPageTypography variant={'h3'}>{ticket.label[language]}</OrderPageTypography>
        <OrderPageTypography variant={'h6'} color="primary.main" bold>
          {ticket.default_ticket_group_size
            ? customerFacingPriceFormatter(
                withBookingFee(unitPriceFor(ticket.id) * ticket.default_ticket_group_size, orderState.account),
                orderState.account,
              )
            : t('multipleTicketsUiObject.perperson', {
                price: customerFacingPriceFormatter(
                  withBookingFee(unitPriceFor(ticket.id), orderState.account),
                  orderState.account,
                ),
              })}
        </OrderPageTypography>
      </Grid>
      {!ticket.default_ticket_group_size && (
        <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <NumberSelector
            isRemoveDisabled={ticketValues[ticket.id] === 0}
            isAddButtonDisabled={isAtMaxParticipants}
            value={ticketValues[ticket.id]}
            setValue={(val: number) => handleValueChange(ticket.id, val)}
            disabled={isLoadingDates}
            min={0}
            max={ticket.capacity ?? uiObject.max_total_participants}
          />
        </Grid>
      )}
    </Grid>
  );

  return (
    <Grid container flexDirection="column" mb={3} gap={1}>
      <OrderPageTypography variant="h3" bold>
        {uiObject.label[language]}
      </OrderPageTypography>
      {shouldShowMinAlert && (
        <Alert severity={'warning'} variant={'outlined'} sx={{ maxWidth: '300px' }}>
          <Grid item>
            <OrderPageTypography variant={'body1'} color={'warning'}>
              {uiObject.min_total_alert?.[language]?.replace('{{min}}', uiObject.min_total_participants.toString())}
            </OrderPageTypography>
          </Grid>
        </Alert>
      )}
      <Grid container flexDirection={'column'} gap={3} xs={12}>
        {mainTickets.map(renderTicket)}
        {extraTickets.length > 0 && (
          <>
            <Divider sx={{ my: 1, opacity: 0.5 }} />
            {extraTickets.map(renderTicket)}
          </>
        )}
      </Grid>
      {isAtMaxParticipants && shouldShowMaxAlert && (
        <Alert severity={'warning'} variant={'outlined'} sx={{ maxWidth: '300px' }}>
          <Grid item>
            <OrderPageTypography variant={'body1'} color={'warning'}>
              {uiObject.max_total_alert?.[language]?.replace('{{max}}', uiObject.max_total_participants.toString())}
            </OrderPageTypography>
          </Grid>
        </Alert>
      )}
    </Grid>
  );
};
